import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { FileText, ExternalLink, Download } from "lucide-react";
import { openBrandedPrintWindow } from "@/lib/documentTemplate";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function ClientPortal() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/portal/data").then((r) => setData(r.data)).catch(() => setData({ client: null, projects: [], invoices: [], documents: [] })); }, []);

  if (!data) return <div className="text-muted-foreground p-6">Loading portal…</div>;
  const { client, projects, invoices, documents } = data;

  const printInvoice = (inv) => {
    const rupee = (n) => `₹${Number(n||0).toLocaleString("en-IN")}`;
    const body = `
      <div style="display:flex;justify-content:space-between;gap:24px;margin-bottom:12px">
        <div style="flex:1"><div style="font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#73706A">Billed to</div>
          <div style="font-size:15px;font-weight:600;margin-top:4px">${client?.name || ""}</div>
        </div>
        <div style="text-align:right">
          <div style="font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#73706A">Due Date</div>
          <div style="font-size:13px;font-weight:600;margin-top:4px">${inv.due_date || "—"}</div>
        </div>
      </div>
      <table><thead><tr><th>Description</th><th class="right" style="width:180px">Amount</th></tr></thead>
      <tbody><tr><td>Professional services</td><td class="right">${rupee(inv.amount)}</td></tr>
      <tr><td>GST / Tax</td><td class="right">${rupee(inv.tax)}</td></tr></tbody></table>
      <div style="margin-top:12px;margin-left:auto;width:280px">
        <div style="display:flex;justify-content:space-between;padding:12px 0;border-top:2px solid #1A1918;font-weight:700;font-size:16px"><span>TOTAL DUE</span><span>${rupee(inv.total)}</span></div>
      </div>
      ${inv.notes ? `<div style="margin-top:20px;padding:12px;background:#F7F5F0;border-radius:6px;font-size:11px;color:#3A3936">${inv.notes}</div>` : ""}
    `;
    openBrandedPrintWindow(body, {
      subject: `Invoice ${inv.number}`,
      title: "Tax Invoice",
      number: inv.number,
      date: new Date(inv.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" }),
    });
  };

  return (
    <div className="space-y-6" data-testid="portal-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Client Portal</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold">{client ? `Hello, ${client.name}` : "Client Portal"}</h1>
        <p className="text-muted-foreground mt-2 text-sm">{client ? "A calm, transparent view of every milestone, drawing, and invoice." : "No linked client account found for your email. Contact your studio admin."}</p>
      </div>

      {client && (
        <>
          <div className="grid md:grid-cols-2 gap-5" data-testid="portal-projects">
            {projects.length === 0 && <div className="text-sm text-muted-foreground p-6 rounded-2xl bg-card border border-border/60">No active projects yet.</div>}
            {projects.map((p) => (
              <div key={p.id} className="rounded-2xl p-6 bg-card border border-border/60" data-testid={`portal-project-${p.id}`}>
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{p.code}</div>
                <div className="font-display text-2xl mt-1">{p.name}</div>
                <p className="text-sm text-muted-foreground mt-1">{p.location}</p>
                <div className="mt-5">
                  <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5"><span>Progress</span><span>{p.progress}%</span></div>
                  <Progress value={p.progress} className="h-2" />
                </div>
                <div className="mt-5 flex gap-2">
                  <Link to={`/projects/${p.id}`} data-testid={`portal-drawings-${p.id}`}>
                    <Button variant="outline" size="sm" className="text-xs"><FileText className="w-3.5 h-3.5 mr-1" /> Drawings & Details</Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl p-6 bg-card border border-border/60" data-testid="portal-invoices">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">Payment Schedule</div>
            {invoices.length === 0 && <div className="text-sm text-muted-foreground">No invoices yet.</div>}
            <div className="divide-y divide-border/50">
              {invoices.map((i) => (
                <div key={i.id} className="py-3 flex items-center justify-between" data-testid={`portal-invoice-${i.id}`}>
                  <div><div className="text-sm font-medium">{i.number}</div><div className="text-xs text-muted-foreground">Due {i.due_date || "—"}</div></div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium">{fmt(i.total)}</span>
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full ${i.status==="paid"?"bg-accent/20 text-accent":i.status==="overdue"?"bg-destructive/15 text-destructive":"bg-primary/15 text-primary"}`}>{i.status}</span>
                    <button onClick={() => printInvoice(i)} data-testid={`portal-invoice-download-${i.id}`} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary" title="Download PDF"><Download className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {documents.length > 0 && (
            <div className="rounded-2xl p-6 bg-card border border-border/60" data-testid="portal-documents">
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">Shared Documents</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {documents.map(d => (
                  <div key={d.id} className="rounded-lg border border-border/50 p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded bg-muted flex items-center justify-center"><FileText className="w-4 h-4 text-muted-foreground" /></div>
                    <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{d.title || d.original_name}</div><div className="text-[11px] text-muted-foreground truncate">{new Date(d.created_at).toLocaleDateString()}</div></div>
                    <a href={d.file_path} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-muted"><ExternalLink className="w-3.5 h-3.5" /></a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
